import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { CheckCircle2, AlertCircle, Info, X } from "lucide-react";
import { cn } from "@/lib/utils";

type ToastKind = "success" | "error" | "info" | "danger";

interface ToastAction {
  label: string;
  onClick: () => void;
}

interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
  action?: ToastAction;
  duration?: number;
}

interface ToastContextValue {
  push: (kind: ToastKind, message: string) => void;
  success: (m: string) => void;
  error: (m: string) => void;
  info: (m: string) => void;
  danger: (m: string) => void;
  notify: (opts: {
    kind?: ToastKind;
    message: string;
    action?: ToastAction;
    duration?: number;
  }) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((kind: ToastKind, message: string) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, kind, message }]);
  }, []);

  const notify = useCallback(
    (opts: {
      kind?: ToastKind;
      message: string;
      action?: ToastAction;
      duration?: number;
    }) => {
      const id = Date.now() + Math.random();
      setToasts((prev) => [
        ...prev,
        {
          id,
          kind: opts.kind ?? "success",
          message: opts.message,
          action: opts.action,
          duration: opts.duration,
        },
      ]);
    },
    [],
  );

  // Valeur stable : les pages mettent `toast` dans leurs dépendances d'effet,
  // une nouvelle référence à chaque rendu relancerait leurs requêtes.
  const value = useMemo<ToastContextValue>(
    () => ({
      push,
      success: (m) => push("success", m),
      error: (m) => push("error", m),
      info: (m) => push("info", m),
      danger: (m) => push("danger", m),
      notify,
    }),
    [push, notify],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        className="fixed bottom-4 right-4 max-sm:left-4 max-lg:bottom-[calc(4.5rem+env(safe-area-inset-bottom))] z-[60] flex flex-col gap-2 max-w-sm"
      >
        {toasts.map((t) => (
          <ToastItem
            key={t.id}
            toast={t}
            onClose={() =>
              setToasts((prev) => prev.filter((x) => x.id !== t.id))
            }
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastItem({ toast, onClose }: { toast: Toast; onClose: () => void }) {
  useEffect(() => {
    const t = setTimeout(onClose, toast.duration ?? 4000);
    return () => clearTimeout(t);
  }, [onClose, toast.duration]);

  const Icon =
    toast.kind === "success" || toast.kind === "danger"
      ? CheckCircle2
      : toast.kind === "error"
        ? AlertCircle
        : Info;

  return (
    <div
      className={cn(
        "surface-elevated px-4 py-3 flex items-start gap-3 text-sm",
        toast.kind === "success" && "border-l-4 border-l-[#16a34a]",
        toast.kind === "error" && "border-l-4 border-l-[var(--color-coral-500)]",
        toast.kind === "info" && "border-l-4 border-l-[var(--color-navy-600)]",
        toast.kind === "danger" && "border-l-4 border-l-[var(--color-danger)]",
      )}
    >
      <Icon
        size={18}
        className={cn(
          "mt-[1px] flex-shrink-0",
          toast.kind === "success" && "text-[#16a34a]",
          toast.kind === "error" && "text-[var(--color-coral-500)]",
          toast.kind === "info" && "text-[var(--color-navy-600)]",
          toast.kind === "danger" && "text-[var(--color-danger)]",
        )}
      />
      <p className="flex-1 text-[var(--color-foreground)] leading-snug">
        {toast.message}
      </p>
      {toast.action && (
        <button
          type="button"
          onClick={() => {
            toast.action!.onClick();
            onClose();
          }}
          className="font-semibold text-[var(--color-navy-700)] hover:text-[var(--color-coral-600)] underline underline-offset-2 flex-shrink-0"
        >
          {toast.action.label}
        </button>
      )}
      <button
        type="button"
        onClick={onClose}
        aria-label="Fermer"
        className="text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] flex-shrink-0"
      >
        <X size={14} />
      </button>
    </div>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}
